import * as React from 'react'
import { ApplicationSettings } from 'bl-health/types'
import {
  Delivered,
  FinalInsuranceValidatingCardUpload,
  InsuranceValidatingCardUpload,
  PrescriptionInvalid,
  PrescriptionProviderFaxMissing,
  PrescriptionProviderMissing,
  PreShipNotice,
  ProviderMissing,
  Shipped,
} from './blocks'
import PrescriptionFaxing from './blocks/PrescriptionFaxing'

interface Block {
  key: string
  component: React.ComponentType<any>
  props: OrderProps | null
}

interface OrderProps {
  order: APIResponse.DME.Order
}

export const getBlocks = (
  order: APIResponse.DME.Order,
  uiSettings: ApplicationSettings | null = null
) =>
  Object.entries(blocks).reduce<Block[]>(
    (result, [key, { component, predicate, getProps }]) =>
      predicate(order, uiSettings)
        ? result.concat({
            key,
            component,
            props: getProps ? getProps(order) : null,
          })
        : result,
    []
  )

const blocks: {
  [key: string]: {
    component: React.ComponentType<any>
    getProps?: (order: APIResponse.DME.Order) => OrderProps
    predicate: (
      order: APIResponse.DME.Order,
      uiSettings?: ApplicationSettings | null
    ) => boolean
  }
} = {
  finalInsuranceValidatingCardUpload: {
    component: FinalInsuranceValidatingCardUpload,
    getProps: (order) => ({ order }),
    predicate: ({ insuranceStatus, awaitingFinalInsuranceCall }) =>
      insuranceStatus === 'validating' && awaitingFinalInsuranceCall,
  },

  insuranceValidatingCardUpload: {
    component: InsuranceValidatingCardUpload,
    getProps: (order) => ({ order }),
    predicate: ({ insuranceStatus, awaitingFinalInsuranceCall }) =>
      insuranceStatus === 'validating' && !awaitingFinalInsuranceCall,
  },

  providerMissing: {
    component: ProviderMissing,
    predicate: ({ providerRequired, providerMissing }) =>
      providerRequired && providerMissing,
  },

  prescriptionFaxing: {
    component: PrescriptionFaxing,
    predicate: (order) =>
      order.prescriptionRequired &&
      order.prescriptionMissing &&
      // Assume we are faxing if there are no states that would
      // block us from doing so.
      !blocks.prescriptionInvalid.predicate(order) &&
      !blocks.prescriptionProviderMissing.predicate(order) &&
      !blocks.prescriptionProviderFaxMissing.predicate(order),
  },

  prescriptionInvalid: {
    component: PrescriptionInvalid,
    predicate: ({ prescriptionRequired, prescriptionInvalid }) =>
      prescriptionRequired && prescriptionInvalid,
  },

  prescriptionProviderMissing: {
    component: PrescriptionProviderMissing,
    predicate: ({
      prescriptionRequired,
      prescriptionMissing,
      providerMissing,
    }) => prescriptionRequired && prescriptionMissing && providerMissing,
  },

  prescriptionProviderFaxMissing: {
    component: PrescriptionProviderFaxMissing,
    predicate: ({
      prescriptionRequired,
      prescriptionMissing,
      providerMissing,
      providerFaxMissing,
    }) =>
      prescriptionRequired &&
      prescriptionMissing &&
      !providerMissing &&
      providerFaxMissing,
  },

  preShipNotice: {
    component: PreShipNotice,
    predicate: ({
      insuranceStatus,
      prescriptionRequired,
      prescriptionMissing,
      providerRequired,
      providerMissing,
      status,
    }) =>
      insuranceStatus === 'valid' &&
      status === 'ordered' &&
      !(prescriptionRequired && prescriptionMissing) &&
      !(providerRequired && providerMissing),
  },

  shipped: {
    component: Shipped,
    predicate: ({ status }) => status === 'shipped',
  },

  delivered: {
    component: Delivered,
    predicate: ({ status }) => status === 'delivered',
  },
}
